import { createContext, useContext, type ReactNode } from 'react';
import type { MyAccessDto } from '../api/types';

/**
 * Permisos del usuario en la contabilidad (Usuarios y roles). Mientras se cargan, o si la API no los informa,
 * no se oculta nada: la API igual valida cada operación.
 */
const AccessContext = createContext<MyAccessDto | null>(null);

export function AccessProvider({ value, children }: { value: MyAccessDto | null; children?: ReactNode }) {
  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

export function useAccess(): MyAccessDto | null {
  return useContext(AccessContext);
}

/** true si el usuario tiene el permiso (o si todavía no se conocen sus permisos). */
export function useCan(permission?: string): boolean {
  const access = useContext(AccessContext);
  return canWith(access, permission);
}

export function canWith(access: MyAccessDto | null, permission?: string): boolean {
  if (!permission || !access) return true;
  return access.permissions.includes(permission);
}

export const PERM = {
  view: 'ACCOUNTING_VIEW',
  setup: 'ACCOUNTING_SETUP',
  entryCreate: 'ACCOUNTING_ENTRY_CREATE',
  entryApply: 'ACCOUNTING_ENTRY_APPLY',
  entryCancel: 'ACCOUNTING_ENTRY_CANCEL',
  periodClose: 'ACCOUNTING_PERIOD_CLOSE',
  reports: 'ACCOUNTING_REPORTS',
  arView: 'AR_VIEW',
  apView: 'AP_VIEW',
  bankView: 'BANK_VIEW',
  cashView: 'CASH_VIEW',
  cashOperate: 'CASH_OPERATE',
  payrollView: 'PAYROLL_VIEW',
  securityAdmin: 'ACCOUNTING_SECURITY_ADMIN',
  auditView: 'ACCOUNTING_AUDIT_VIEW',
} as const;
